import React, { forwardRef, useState, useRef, useEffect } from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { createStitches } from '@stitches/react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as RadixLabel from '@radix-ui/react-label';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { uniqueId } from 'lodash';
import * as Tooltip$1 from '@radix-ui/react-tooltip';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var _a$6;
var ForAudioScrub = {
    primaryFill: '#ffffff',
    primaryBorder: 'rgba(255, 255, 255, 0.1)',
};
var DarkTheme = {
    // background
    $colors$primaryBackground: '#101010',
    $colors$secondaryBackground: '#181818',
    // fill
    $colors$primaryFill: '#ffffff',
    $colors$secondaryFill: '#8c8c8d',
    // other
    $colors$primaryHighlight: '#0971f1',
    $colors$secondaryHighlight: '#227ff2',
    $colors$primaryBorder: '#282828',
    $colors$secondaryBorder: '#404040',
    $colors$primaryError: '#f9153e',
    $colors$secondaryError: '#190206',
};
var LightTheme = {
    // background
    $colors$primaryBackground: '#ffffff',
    $colors$secondaryBackground: '#f7f7f7',
    // fill
    $colors$primaryFill: '#000000',
    $colors$secondaryFill: 'rgba(0, 0, 0, 0.6)',
    // other
    $colors$primaryHighlight: '#0971f1',
    $colors$secondaryHighlight: '#227ff2',
    $colors$primaryBorder: '#e5e5e5',
    $colors$secondaryBorder: '#cccccc',
    $colors$primaryError: '#f9153e',
    $colors$secondaryError: '#190206',
};
var theme = (_a$6 = createStitches({
    theme: {
        fonts: { DMSans: 'DM Sans, sans-serif', mono: 'DM Mono, monospace' },
        space: {
            0: '128px',
            1: '64px',
            2: '32px',
            3: '16px',
            4: '8px',
            5: '4px',
        },
        transitions: {
            fast: 'all 0.1s ease-in-out',
            medium: 'all 0.2s ease-in-out',
            slow: 'all 0.5s ease-in-out',
        },
        colors: __assign({ green: '#4ec74e', yellow: '#FFB200', red: '#F9153E' }, ForAudioScrub),
    },
    media: {
        bp1: '(min-width: 640px)',
        bp2: '(min-width: 1440px)', // large
    },
}), _a$6.theme), styled = _a$6.styled, getCssText = _a$6.getCssText, css = _a$6.css, keyframes = _a$6.keyframes, globalCss = _a$6.globalCss;

var slideDown = keyframes({
    from: { height: 0 },
    to: { height: 'var(--radix-accordion-content-height)' },
});
var slideUp = keyframes({
    from: { height: 'var(--radix-accordion-content-height)' },
    to: { height: 0 },
});
var Root$2 = styled('div', {});
var Trigger = styled('button', {
    all: 'unset',
    backgroundColor: 'transparent',
    padding: '0 $space$4',
    height: 40,
    flex: 1,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '$secondaryFill',
    cursor: 'pointer',
    '&[data-state="closed"]': { backgroundColor: 'transparent' },
    '&[data-state="open"]': { backgroundColor: '$secondaryBackground', color: '$primaryFill' },
    '&:hover': { backgroundColor: '$secondaryBackground', color: '$primaryFill' },
});
var Item$2 = styled('div', {
    overflow: 'hidden',
});
var Header$2 = styled('h3', {
    all: 'unset',
    display: 'flex',
});
var Content$2 = styled('div', {
    overflow: 'hidden',
    color: '$primaryFill',
    padding: '$space$4',
    '&[data-state="open"]': {
        animation: "".concat(slideDown, " 200ms linear"),
    },
    '&[data-state="closed"]': {
        animation: "".concat(slideUp, " 200ms linear"),
    },
});
var Chevron = styled('div', {
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 200ms linear',
    i: {
        color: '$secondaryFill',
    },
    '[data-state=open] &': { transform: 'rotate(180deg)' },
});

var AccordionTrigger = forwardRef(function (_a, forwardedRef) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (React.createElement(AccordionPrimitive.Header, { asChild: true },
        React.createElement(Header$2, null,
            React.createElement(AccordionPrimitive.Trigger, { asChild: true },
                React.createElement(Trigger, __assign({}, props, { ref: forwardedRef }),
                    children,
                    React.createElement(Chevron, { "aria-hidden": true },
                        React.createElement("i", null, "expand_more")))))));
});
AccordionTrigger.displayName = 'accordionTrigger';
var AccordionContent = forwardRef(function (_a, forwardedRef) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (React.createElement(AccordionPrimitive.Content, __assign({}, props, { ref: forwardedRef, asChild: true }),
        React.createElement(Content$2, null,
            React.createElement("div", null, children))));
});
AccordionContent.displayName = 'accordionContent';
var AccordionItem = function (_a) {
    var children = _a.children, value = _a.value;
    return (React.createElement(AccordionPrimitive.Item, { value: value, asChild: true },
        React.createElement(Item$2, null, children)));
};
AccordionItem.displayName = 'accordionItem';
var Accordion = function (_a) {
    var children = _a.children;
    return (React.createElement(AccordionPrimitive.Root, { type: "multiple", asChild: true },
        React.createElement(Root$2, null, children)));
};

var Button$1 = styled('button', {
    appearance: 'none',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    lineHeight: 1.2,
    color: '$primaryFill',
    borderRadius: 5,
    padding: '$space$4 $space$3',
    fontFamily: '$DMSans',
    fontSize: 14,
    cursor: 'pointer',
    transition: '$fast',
    overflow: 'hidden',
    i: {
        color: 'inherit',
    },
    '&:focus': {
        outline: 0,
    },
    '&:disabled': {
        cursor: 'not-allowed',
        // opacity: 0.5,
        // pointerEvents: 'none',
    },
    variants: {
        full: {
            true: {
                width: '100%',
                height: 40,
                fontSize: 16,
                fontWeight: 700,
            },
        },
        round: {
            true: {
                borderRadius: 9999,
            },
        },
        small: {
            true: {
                padding: '$space$5',
            },
        },
        shrink: {
            true: {
                alignSelf: 'flex-start',
            },
        },
        variant: {
            primary: {
                fontWeight: 700,
                backgroundColor: '$primaryFill',
                color: '$primaryBackground',
                '&:hover ': {
                    backgroundColor: 'transparent',
                    border: '1px solid $colors$primaryFill',
                    color: '$primaryFill',
                },
                '&:focus': {
                    backgroundColor: 'transparent',
                    border: '1px solid $colors$primaryFill',
                    color: '$primaryFill',
                },
                '&:disabled': {
                    backgroundColor: 'transparent',
                    border: '1px solid $colors$primaryBorder',
                    color: '$secondaryFill',
                },
            },
            secondary: {
                border: '1px solid $colors$primaryBorder',
                color: '$secondaryFill',
                '&:hover ': {
                    border: '1px solid $colors$primaryFill',
                    color: '$primaryFill',
                },
                '&:focus': {
                    border: '1px solid $colors$primaryFill',
                    color: '$primaryFill',
                },
                '&:disabled': {
                    border: '1px solid $colors$primaryBorder',
                    color: '$secondaryFill',
                },
            },
            highlight: {
                border: '1px solid $colors$primaryHighlight',
                color: '$primaryHighlight',
                '&:hover ': {
                    backgroundColor: '$primaryHighlight',
                    color: '$primaryFill',
                },
                '&:focus': {
                    backgroundColor: '$primaryHighlight',
                    color: '$primaryFill',
                },
                '&:disabled': {
                    backgroundColor: '$primaryHighlight',
                    color: '$primaryFill',
                },
            },
            warning: {
                border: '1px solid $colors$red',
                color: '$red',
                '&:hover ': {
                    backgroundColor: '$red',
                    color: '$primaryFill',
                },
                '&:focus': {
                    backgroundColor: '$red',
                    color: '$primaryFill',
                },
                '&:disabled': {
                    backgroundColor: '$red',
                    color: '$primaryFill',
                },
            },
            simple: {
                color: '$secondaryFill',
                '&:hover ': {
                    color: '$primaryFill',
                },
                '&:focus': {
                    color: '$primaryFill',
                },
            },
            basic: {
                padding: 0,
                color: '$secondaryFill',
                '&:hover ': {
                    color: '$primaryFill',
                },
                '&:focus ': {
                    color: '$primaryFill',
                },
            },
            market: {
                backgroundColor: 'transparent',
                color: '$secondaryFill',
                padding: '$space$5 $space$4',
                borderRadius: 5,
                fontSize: 12,
                lineHeight: 'unset',
                border: '1px solid $colors$primaryBorder',
                '&:hover': {
                    border: '1px solid $colors$secondaryBorder',
                },
                '&:focus-within': {
                    border: '1px solid $colors$primaryFill',
                },
            },
            tag: {
                backgroundColor: '$secondaryBackground',
                color: '$secondaryFill',
                padding: '$space$5 $space$4',
                borderRadius: 5,
                fontSize: 12,
                border: '1px solid $colors$primaryBorder',
                '&:hover': {
                    border: '1px solid $colors$secondaryBorder',
                },
                '&:focus-within': {
                    border: '1px solid $colors$primaryFill',
                },
            },
            underline: {
                padding: 0,
                color: '$secondaryFill',
                textDecoration: 'underline',
                '&:hover ': {
                    textDecoration: 'none',
                },
                '&:focus ': {
                    textDecoration: 'none',
                },
            },
        },
    },
    defaultVariants: {
        variant: 'primary',
    },
});

var fadeInSlide$1 = keyframes({
    '0%': { opacity: 0, transform: 'translate(-50%, calc(-50% + 20px))' },
    '100%': { opacity: 1, transform: 'translate(-50%, -50%)' },
});
var fadeIn$1 = keyframes({
    from: { opacity: 0 },
    to: { opacity: 1 },
});
var fadeOut$1 = keyframes({
    from: { opacity: 1 },
    to: { opacity: 0 },
});
// const scaleIn = keyframes({
//   from: { transform: 'scale(0.9)' },
//   to: { transform: 'scale(1)' },
// });
var Overlay$2 = styled('div', {
    backgroundColor: 'hsl(0 0% 0% / 0.5439)',
    position: 'fixed',
    top: 0,
    inset: 0,
    zIndex: 2000,
    pointerEvents: 'all',
    '@media (prefers-reduced-motion: no-preference)': {
        animation: "".concat(fadeIn$1, " 200ms ease-out"),
    },
    variants: {
        opaque: {
            true: {
                backgroundColor: 'hsl(0 0% 0% / 1)',
            },
        },
    },
});
var Header$1 = styled('div', {
    marginBottom: '$space$4',
});
var Close$1 = styled('div', {
    position: 'absolute',
    top: 16,
    right: 16,
    cursor: 'pointer',
    background: 'transparent',
    border: 0,
    i: {
        color: '$secondaryFill',
    },
    '&:hover': {
        i: {
            color: '$primaryFill',
        },
    },
    '&:focus': {
        outline: 0,
        i: {
            color: '$primaryFill',
        },
    },
});
var Title$1 = styled('h6', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    color: '$primaryFill',
    width: 'calc(100% - 24px)',
    height: 24,
    margin: 0,
});
var Description$1 = styled('p', {
    color: '$secondaryFill',
    margin: '$space$5 0 0 0',
    fontSize: 14,
    width: 'calc(100% - 24px)',
});
var Content$1 = styled('div', {
    backgroundColor: '$primaryBackground',
    border: '1px solid $colors$primaryBorder',
    borderRadius: 5,
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: 450,
    // maxHeight: '85vh',
    padding: '$space$3',
    '&:focus': { outline: 'none' },
    '@media (prefers-reduced-motion: no-preference)': {
        animation: "".concat(fadeInSlide$1, " 200ms ease-out"),
        willChange: 'transform',
        '&[data-state="closed"]': {
            animation: "".concat(fadeOut$1, " 100ms ease-out"),
        },
    },
    variants: {
        fill: {
            true: {
                padding: 'unset',
            },
        },
        large: {
            true: {
                maxWidth: 720,
                maxHeight: 'unset',
            },
        },
    },
});
var Buttons$1 = styled('div', {
    '& > *': {
        marginTop: '$space$4',
    },
});

var AlertDialog = function (_a) {
    var title = _a.title, description = _a.description, trigger = _a.trigger, children = _a.children, open = _a.open, large = _a.large, fill = _a.fill, opaque = _a.opaque, onOpenChange = _a.onOpenChange, onConfirm = _a.onConfirm, onCancel = _a.onCancel, _b = _a.canClose, canClose = _b === void 0 ? true : _b, confirmText = _a.confirmText, cancelText = _a.cancelText, _c = _a.warning, warning = _c === void 0 ? false : _c;
    return (React.createElement(AlertDialogPrimitive.Root, { open: open, onOpenChange: onOpenChange, allowPinchZoom: true },
        trigger && React.createElement(AlertDialogPrimitive.Trigger, { asChild: true }, trigger),
        React.createElement(AlertDialogPrimitive.Overlay, { asChild: true },
            React.createElement(Overlay$2, { opaque: opaque },
                React.createElement(AlertDialogPrimitive.Content, null,
                    React.createElement(Content$1, { large: large, fill: fill },
                        (title || description || canClose) && (React.createElement(Header$1, null,
                            title && (React.createElement(AlertDialogPrimitive.Title, { asChild: true },
                                React.createElement(Title$1, null, title))),
                            description && (React.createElement(AlertDialogPrimitive.Description, { asChild: true },
                                React.createElement(Description$1, null, description))),
                            canClose && (React.createElement(AlertDialogPrimitive.Cancel, { asChild: true },
                                React.createElement(Close$1, null,
                                    React.createElement(Button$1, { variant: "basic", onClick: onCancel },
                                        React.createElement("i", null, "close"))))))),
                        children,
                        React.createElement(Buttons$1, null,
                            onConfirm && (React.createElement(AlertDialogPrimitive.Action, { asChild: true },
                                React.createElement(Button$1, __assign({ full: true }, (warning && { variant: 'warning' }), { onClick: onConfirm }), confirmText || 'Confirm'))),
                            onCancel && (React.createElement(AlertDialogPrimitive.Cancel, { asChild: true },
                                React.createElement(Button$1, { full: true, variant: "secondary", onClick: onCancel }, cancelText || 'Cancel'))))))))));
};

var _a$5;
var Thumb$1 = styled('span', {
    all: 'unset',
    display: 'block',
    height: 12,
    width: 12,
    borderRadius: 9999,
    background: '$primaryFill',
    opacity: 0,
    transition: '$fast',
    '&:focus': {
        opacity: 1,
    },
    variants: {
        scrubber: {
            true: {
                opacity: 1,
            },
        },
    },
});
var Root$1 = styled('span', {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    userSelect: 'none',
    touchAction: 'none',
    width: '100%',
    '&[data-orientation="horizontal"]': {
        height: 20,
    },
    '&:hover': (_a$5 = {},
        _a$5["& ".concat(Thumb$1)] = { opacity: 1 },
        _a$5),
});
var Track = styled('span', {
    backgroundColor: '$primaryBorder',
    position: 'relative',
    flexGrow: 1,
    borderRadius: 9999,
    '&[data-orientation="horizontal"]': { height: 4, borderRadius: 2 },
});
var Range = styled('span', {
    position: 'absolute',
    backgroundColor: '$primaryFill',
    borderRadius: 9999,
    height: '100%',
});

var Slider = function (_a) {
    var value = _a.value, label = _a.label, _b = _a.min, min = _b === void 0 ? 0 : _b, _c = _a.max, max = _c === void 0 ? 1 : _c, _d = _a.step, step = _d === void 0 ? 0.01 : _d, _e = _a.onChange, onChange = _e === void 0 ? function () { return null; } : _e, _f = _a.scrubber, scrubber = _f === void 0 ? false : _f;
    var handleChange = function (_a) {
        var nextValue = _a[0];
        onChange(nextValue);
    };
    return (React.createElement(SliderPrimitive.Root, { asChild: true, min: min, max: max, step: step, "aria-label": label, value: value, onValueChange: handleChange },
        React.createElement(Root$1, null,
            React.createElement(SliderPrimitive.Track, { asChild: true },
                React.createElement(Track, null,
                    React.createElement(SliderPrimitive.Range, { asChild: true },
                        React.createElement(Range, null)))),
            React.createElement(SliderPrimitive.Thumb, { asChild: true },
                React.createElement(Thumb$1, { scrubber: scrubber })))));
};
Slider.displayName = 'SliderComponent';

var Text = styled('p', {
    color: '$primaryFill',
    variants: {
        unset: {
            true: {
                margin: 'unset',
            },
        },
        ellipsis: {
            true: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
        },
    },
});

var Controls = styled('div', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    i: {
        cursor: 'pointer',
        color: '$secondaryFill',
        transition: '$fast',
        '&:hover': {
            color: '$primaryFill',
        },
    },
});
var Main = styled('button', {
    backgroundColor: 'transparent',
    border: 'none',
    i: {
        fontSize: 48,
    },
    '&:focus': {
        outline: 0,
        i: {
            color: '$primaryFill',
        },
    },
});
var Secondary = styled('button', {
    backgroundColor: 'transparent',
    border: 'none',
    margin: '$space$3',
    i: {
        fontSize: 24,
    },
    '&:focus': {
        outline: 0,
        i: {
            color: '$primaryFill',
        },
    },
});
var Scrub = styled('div', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: 'calc(100vw - $space$3 - $space$3)',
    margin: '0 auto',
    '@bp1': {
        maxWidth: 'calc(100vw - $space$1 - $space$1)',
    },
});

var AudioPlayer = function (_a) {
    var audioUri = _a.audioUri, onPlayPauseChange = _a.onPlayPauseChange;
    var UPDATE_RATE = 350;
    var DEFAULT_FAST_DIVIDER = 10;
    var _b = useState(false), isPlaying = _b[0], setIsPlaying = _b[1];
    var audioRef = useRef(typeof Audio !== 'undefined' ? new Audio(audioUri) : undefined);
    var intervalRef = useRef(0);
    var volumeRef = useRef(1);
    var _c = useState(0), scrubValue = _c[0], setScrubValue = _c[1];
    var _d = useState(1), scrubTotal = _d[0], setScrubTotal = _d[1];
    var getSecondsToTime = function (seconds) {
        if (seconds === void 0) { seconds = 1; }
        if (seconds < 3600) {
            // mm:ss
            return new Date(seconds * 1000).toISOString().substr(14, 5);
        }
        // hh:mm:ss
        return new Date(seconds * 1000).toISOString().substr(11, 8);
    };
    var startTimer = function () {
        // Clear any timers already running
        clearInterval(intervalRef.current);
        intervalRef.current = window.setInterval(function () {
            var _a, _b, _c;
            var cur = ((_a = audioRef.current) === null || _a === void 0 ? void 0 : _a.currentTime) || 0;
            var total = ((_b = audioRef.current) === null || _b === void 0 ? void 0 : _b.duration) || 1;
            if (!((_c = audioRef.current) === null || _c === void 0 ? void 0 : _c.ended)) {
                setScrubValue(cur);
                setScrubTotal(total);
            }
        }, UPDATE_RATE);
    };
    var onScrubChange = function (nextValue) {
        var v = Number(nextValue);
        if (audioRef.current) {
            audioRef.current.currentTime = v;
        }
    };
    var onFastRewind = function () {
        if (audioRef.current) {
            var amount = audioRef.current.duration / DEFAULT_FAST_DIVIDER;
            audioRef.current.currentTime -= amount;
        }
    };
    var onFastForward = function () {
        if (audioRef.current) {
            var amount = audioRef.current.duration / DEFAULT_FAST_DIVIDER;
            audioRef.current.currentTime += amount;
        }
    };
    useEffect(function () {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.play();
                startTimer();
                if (onPlayPauseChange) {
                    onPlayPauseChange(true);
                }
            }
            else {
                audioRef.current.pause();
                if (onPlayPauseChange) {
                    onPlayPauseChange(false);
                }
            }
        }
    }, [isPlaying]);
    useEffect(function () {
        var _a;
        // onmount / change track
        (_a = audioRef.current) === null || _a === void 0 ? void 0 : _a.pause();
        if (onPlayPauseChange) {
            onPlayPauseChange(false);
        }
        var onDurationChange = function () { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (audioRef.current) {
                    setScrubValue(audioRef.current.currentTime);
                    setScrubTotal(audioRef.current.duration);
                }
                return [2 /*return*/];
            });
        }); };
        audioRef.current = new Audio(audioUri);
        audioRef.current.volume = volumeRef.current;
        audioRef.current.addEventListener('durationchange', onDurationChange);
        // unmount
        return function () {
            var _a;
            (_a = audioRef.current) === null || _a === void 0 ? void 0 : _a.pause();
            clearInterval(intervalRef.current);
            if (audioRef.current) {
                audioRef.current.removeEventListener('durationchange', onDurationChange);
            }
        };
    }, [audioUri]);
    return (React.createElement("div", null,
        React.createElement(Controls, null,
            false ,
            React.createElement(Secondary, { onClick: onFastRewind },
                React.createElement("i", null, "fast_rewind")),
            React.createElement(Main, { onClick: function () { return setIsPlaying(!isPlaying); } }, isPlaying ? React.createElement("i", null, "pause_circle_filled") : React.createElement("i", null, "play_circle_filled")),
            React.createElement(Secondary, { onClick: onFastForward },
                React.createElement("i", null, "fast_forward")),
            false ),
        React.createElement(Scrub, { css: { width: '100%' } },
            React.createElement(Text, { unset: true, css: { fontFamily: '$mono', fontSize: 12, color: '$secondaryFill', marginRight: '$4' } }, getSecondsToTime(scrubValue)),
            React.createElement(Slider, { value: [scrubValue], max: scrubTotal, label: "audio-scrub", onChange: onScrubChange }),
            React.createElement(Text, { unset: true, css: { fontFamily: '$mono', fontSize: 12, color: '$secondaryFill', marginLeft: '$4' } },
                "-",
                getSecondsToTime(scrubTotal - scrubValue)))));
};

var Box = styled('div', {
    display: 'flex',
    variants: {
        vertical: {
            true: {
                alignItems: 'center',
            },
        },
        horizontal: {
            true: {
                justifyContent: 'center',
            },
        },
        mobile: {
            true: {
                '@bp2': {
                    display: 'none',
                },
            },
        },
        tablet: {
            true: {
                display: 'none',
                width: '100%',
                '@bp1': {
                    display: 'block',
                },
            },
        },
        desktop: {
            true: {
                display: 'none',
                width: '100%',
                '@bp2': {
                    display: 'block',
                },
            },
        },
    },
});

var _a$4, _b$1, _c, _d;
var Container$g = styled('button', {
    all: 'unset',
    backgroundColor: '$secondaryBackground',
    width: 16,
    height: 16,
    borderRadius: 5,
    border: '1px solid $colors$primaryBorder',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});
var Indicator = styled('div', {
    i: {
        display: 'block',
        fontSize: 14,
        fontWeight: 700,
        color: '$secondaryFill',
    },
});
var Label$5 = styled(RadixLabel.Root, (_a$4 = {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer'
    },
    _a$4["& ".concat(Text)] = {
        fontSize: 14,
        order: 1,
        marginRight: '$space$3',
    },
    _a$4["& ".concat(Container$g)] = {
        order: 2,
    },
    _a$4['&:hover'] = (_b$1 = {},
        _b$1["& ".concat(Container$g)] = {
            border: '1px solid $colors$secondaryBorder',
        },
        _b$1),
    _a$4['&:focus-within'] = (_c = {},
        _c["& ".concat(Container$g)] = {
            border: '1px solid $colors$primaryFill',
            i: {
                color: '$primaryFill',
            },
        },
        _c),
    _a$4.variants = {
        reversed: {
            true: (_d = {},
                _d["& ".concat(Text)] = {
                    order: 2,
                    marginRight: 'unset',
                    marginLeft: '$space$3',
                },
                _d["& ".concat(Container$g)] = {
                    order: 1,
                },
                _d),
        },
        disabled: {
            true: {
                cursor: 'not-allowed',
            },
        },
    },
    _a$4));

var Checkbox = function (_a) {
    var value = _a.value, onChange = _a.onChange, label = _a.label, _b = _a.reversed, reversed = _b === void 0 ? false : _b, disabled = _a.disabled, rest = __rest(_a, ["value", "onChange", "label", "reversed", "disabled"]);
    return (React.createElement(Label$5, __assign({ reversed: reversed, disabled: disabled }, rest),
        React.createElement(CheckboxPrimitive.Root, { checked: value, onCheckedChange: onChange, asChild: true },
            React.createElement(Container$g, null,
                React.createElement(CheckboxPrimitive.Indicator, { asChild: true },
                    React.createElement(Indicator, null,
                        React.createElement("i", null, "done"))))),
        label && (React.createElement(Text, { unset: true, css: { color: '$secondaryFill' } }, label))));
};

var Container$f = styled('div', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
});
var Button = styled('button', {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    '&:focus': {
        outline: 0,
        i: {
            color: '$primaryFill',
        },
    },
    '&:hover': {
        i: {
            color: '$primaryFill',
        },
    },
});
var Icon$1 = styled('div', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: 5,
    i: {
        fontSize: 14,
        color: '$secondaryFill',
    },
});

var Copy = function (_a) {
    var value = _a.value, children = _a.children;
    var _b = useState(false), copied = _b[0], setCopied = _b[1];
    var timeout;
    var handleCopy = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, navigator.clipboard.writeText(value)];
                case 1:
                    _a.sent();
                    setCopied(true);
                    timeout = window.setTimeout(function () {
                        setCopied(false);
                    }, 2000);
                    return [2 /*return*/];
            }
        });
    }); };
    useEffect(function () {
        return function () {
            clearTimeout(timeout);
        };
    }, []);
    return (React.createElement(Container$f, null,
        React.createElement(Button, { onClick: handleCopy },
            React.createElement(Icon$1, null, copied ? React.createElement("i", null, "done") : React.createElement("i", null, "content_copy"))),
        children));
};

var _a$3;
var Container$e = styled('code', (_a$3 = {
        position: 'relative',
        padding: '$space$3',
        margin: '$space$3 0',
        backgroundColor: '$secondaryBackground',
        borderRadius: 5
    },
    _a$3["& ".concat(Container$f)] = {
        position: 'absolute',
        top: 8,
        right: 8,
    },
    _a$3));

var Code = function (_a) {
    var children = _a.children;
    var formatted = JSON.stringify(children, null, 2);
    return (React.createElement(Container$e, null,
        React.createElement("pre", null, formatted),
        React.createElement(Copy, { value: formatted })));
};

var Container$d = styled('div', {
    padding: '0 $space$3',
    '@bp1': {
        padding: '0 $space$2',
    },
    '@bp2': {
        padding: '0 $space$1',
    },
    variants: {
        centred: {
            true: {
                '@bp2': {
                    maxWidth: 1440,
                    margin: '0 auto',
                },
            },
        },
        debug: {
            true: {
                border: '1px dashed red',
                '@bp1': {
                    border: '1px dashed green',
                },
                '@bp2': {
                    border: '1px dashed blue',
                },
            },
        },
    },
});

var Tezos = styled('span', {
    display: 'inline-flex',
    margin: '0 $space$5',
    '&:after': {
        content: 'tez',
        fontFamily: '$mono',
        color: 'inherit',
        '@bp1': {
            content: 'ꜩ',
        },
    },
});

var fadeInSlide = keyframes({
    '0%': { opacity: 0, transform: 'translate(-50%, calc(-50% + 20px))' },
    '100%': { opacity: 1, transform: 'translate(-50%, -50%)' },
});
var fadeIn = keyframes({
    from: { opacity: 0 },
    to: { opacity: 1 },
});
var fadeOut = keyframes({
    from: { opacity: 1 },
    to: { opacity: 0 },
});
// const scaleIn = keyframes({
//   from: { transform: 'scale(0.9)' },
//   to: { transform: 'scale(1)' },
// });
var Overlay$1 = styled('div', {
    backgroundColor: 'hsl(0 0% 0% / 0.5439)',
    position: 'fixed',
    top: 0,
    inset: 0,
    zIndex: 2000,
    pointerEvents: 'all',
    '@media (prefers-reduced-motion: no-preference)': {
        animation: "".concat(fadeIn, " 200ms ease-out"),
    },
});
var Header = styled('div', {
    marginBottom: 24,
});
var Close = styled('button', {
    position: 'absolute',
    top: 16,
    right: 16,
    cursor: 'pointer',
    background: 'transparent',
    border: 0,
    i: {
        color: '$secondaryFill',
    },
    '&:hover': {
        i: {
            color: '$primaryFill',
        },
    },
    '&:focus': {
        outline: 0,
        i: {
            color: '$primaryFill',
        },
    },
});
var Title = styled('h6', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    color: '$primaryFill',
    height: 24,
    margin: 0,
});
var Description = styled('p', {
    color: '$secondaryFill',
    margin: '$space$5 0 0 0',
    fontSize: 14,
});
var Content = styled('div', {
    backgroundColor: '$primaryBackground',
    border: '1px solid $colors$primaryBorder',
    borderRadius: 5,
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: 450,
    maxHeight: '85vh',
    padding: 16,
    '&:focus': { outline: 'none' },
    '@media (prefers-reduced-motion: no-preference)': {
        animation: "".concat(fadeInSlide, " 200ms ease-out"),
        willChange: 'transform',
        '&[data-state="closed"]': {
            animation: "".concat(fadeOut, " 100ms ease-out"),
        },
    },
    variants: {
        large: {
            true: {
                maxWidth: 1024,
                maxHeight: 'unset',
            },
        },
    },
});
var Buttons = styled('div', {
    '& > *': {
        marginTop: '$space$4',
    },
});

var Dialog = function (_a) {
    var title = _a.title, description = _a.description, trigger = _a.trigger, children = _a.children, open = _a.open, large = _a.large, onOpenChange = _a.onOpenChange, onConfirm = _a.onConfirm, onCancel = _a.onCancel;
    return (React.createElement(DialogPrimitive.Root, { open: open, onOpenChange: onOpenChange, allowPinchZoom: true },
        trigger && React.createElement(DialogPrimitive.Trigger, { asChild: true }, trigger),
        React.createElement(DialogPrimitive.Overlay, { asChild: true },
            React.createElement(Overlay$1, null,
                React.createElement(DialogPrimitive.Content, null,
                    React.createElement(Content, { large: large },
                        React.createElement(Header, null,
                            title && (React.createElement(DialogPrimitive.Title, { asChild: true },
                                React.createElement(Title, null, title))),
                            description && (React.createElement(DialogPrimitive.Description, { asChild: true },
                                React.createElement(Description, null, description))),
                            React.createElement(DialogPrimitive.Close, { asChild: true },
                                React.createElement(Close, null,
                                    React.createElement("i", null, "close")))),
                        children,
                        React.createElement(Buttons, null,
                            onConfirm && (React.createElement(Button$1, { full: true, onClick: onConfirm }, "Confirm")),
                            onCancel && (React.createElement(DialogPrimitive.Close, { asChild: true },
                                React.createElement(Button$1, { full: true, variant: "secondary", onClick: onCancel }, "Cancel"))))))))));
};

var TooltipTrigger = styled(Tooltip$1.Trigger, {
    backgroundColor: 'transparent',
    border: 'none',
    // marginLeft: 4,
    i: {
        color: '$secondaryFill',
        fontSize: 12,
    },
    '&:focus': {
        outline: 0,
    },
});
var TooltipContent = styled(Tooltip$1.Content, {
    borderRadius: 5,
    fontSize: 12,
    padding: '$space$4',
    backgroundColor: '$secondaryBackground',
    border: '1px solid $primaryBorder',
    color: '$secondaryFill',
    maxWidth: 250,
});

var Tooltip = function (_a) {
    var children = _a.children, description = _a.description;
    return (React.createElement(Tooltip$1.Root, { delayDuration: 0 },
        React.createElement(TooltipTrigger, { tabIndex: -1 }, children),
        React.createElement(TooltipContent, { sideOffset: 8 }, description)));
};

var Container$c = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '$space$3',
});
var LabelContainer = styled('div', {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '$space$4',
});
var Label$4 = styled(RadixLabel.Root, {
    cursor: 'default',
    color: '$secondaryFill',
    fontSize: 14,
});
var Error = styled('div', {
    fontSize: 13,
    color: '$primaryError',
    padding: '$space$4',
});

var Field = function (_a) {
    var uid = _a.uid, label = _a.label, tooltip = _a.tooltip, children = _a.children, error = _a.error;
    var internalUID = useState(uniqueId('vfield-'))[0];
    return (React.createElement(Container$c, { id: uid },
        label && (React.createElement(LabelContainer, null,
            React.createElement(Label$4, { htmlFor: internalUID }, label),
            "\u00A0",
            tooltip && (React.createElement(Tooltip, { description: tooltip },
                React.createElement("i", null, "help"))))),
        React.createElement("div", { id: internalUID }, children),
        error && React.createElement(Error, null, error)));
};

var IconButton = styled('button', {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderRadius: 9999,
    border: '1px solid $colors$primaryBorder',
    padding: 4,
    cursor: 'pointer',
    i: {
        color: '$secondaryFill',
        fontSize: 14,
    },
    '&:focus': {
        outline: 0,
        border: '1px solid $colors$primaryFill',
    },
    '&:hover': {
        border: '1px solid $colors$secondaryBorder',
    },
});

// original version https://github.com/varld/gradient-avatar
var stringHash = function (str) {
    var hash = 5381;
    var i = str.length;
    while (i) {
        hash = (hash * 33) ^ str.charCodeAt(--i); // eslint-disable-line
    }
    return hash >>> 0;
};
var hue = function (p, q, t) {
    if (t < 0)
        t += 1;
    if (t > 1)
        t -= 1;
    if (t < 1 / 6)
        return p + (q - p) * 6 * t;
    if (t < 1 / 2)
        return q;
    if (t < 2 / 3)
        return p + (q - p) * (2 / 3 - t) * 6;
    return p;
};
var hslRgb = function (h, s, l) {
    var r;
    var g;
    var b;
    var h_ = h / 360; // eslint-disable-line
    if (s === 0) {
        r = l;
        g = l;
        b = l;
    }
    else {
        var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        var p = 2 * l - q;
        r = hue(p, q, h_ + 1 / 3);
        g = hue(p, q, h_);
        b = hue(p, q, h_ - 1 / 3);
    }
    return [
        Math.max(0, Math.min(Math.round(r * 255), 255)),
        Math.max(0, Math.min(Math.round(g * 255), 255)),
        Math.max(0, Math.min(Math.round(b * 255), 255)),
    ];
};
var hslTriad = function (h, s, l) {
    return [
        [h, s, l],
        [(h + 120) % 360, s, l],
        [(h + 240) % 360, s, l],
    ];
};
var Identicon = function (_a) {
    var _b = _a.hash, hash = _b === void 0 ? 'default' : _b;
    var str = stringHash(hash);
    var colors = hslTriad(str % 360, 1, 0.5);
    var color1 = hslRgb(colors[0][0], colors[0][1], colors[0][2]);
    var color2 = hslRgb(colors[1][0], colors[1][1], colors[1][2]);
    var color1str = "rgb(".concat(color1[0], ", ").concat(color1[1], ", ").concat(color1[2], ")");
    var color2str = "rgb(".concat(color2[0], ", ").concat(color2[1], ", ").concat(color2[2], ")");
    return (React.createElement("svg", { width: "100%", height: "100%", viewBox: "0 0 80 80" },
        React.createElement("defs", null,
            React.createElement("linearGradient", { x1: "0%", y1: "0%", x2: "100%", y2: "100%", id: hash },
                React.createElement("stop", { stopColor: color1str, offset: "0%" }),
                React.createElement("stop", { stopColor: color2str, offset: "100%" }))),
        React.createElement("g", { stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" },
            React.createElement("rect", { fill: "url(#".concat(hash, ")"), x: "0", y: "0", width: "80", height: "80" }))));
};

var _a$2, _b;
var Overlay = styled('div', {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'black',
    opacity: 0.3,
    transition: '$fast',
});
var Container$b = styled('button', {
    appearance: 'none',
    backgroundColor: '$secondaryBackground',
    border: 'none',
    color: 'white',
    fontFamily: '$DMSans',
    fontSize: 14,
    transition: '$fast',
    cursor: 'pointer',
    overflow: 'hidden',
    position: 'relative',
    i: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translateX(-50%) translateY(-50%)',
        fontSize: 14,
        color: 'inherit',
        opacity: 0.8,
        transition: '$fast',
    },
    '&:focus': (_a$2 = {
            outline: 'none'
        },
        _a$2["& ".concat(Overlay)] = {
            opacity: 0,
        },
        _a$2.i = {
            opacity: 1,
        },
        _a$2),
    '&:hover': (_b = {},
        _b["& ".concat(Overlay)] = {
            opacity: 0,
        },
        _b.i = {
            opacity: 1,
        },
        _b),
});

var ImageEdit = function (_a) {
    var children = _a.children, _b = _a.onClick, onClick = _b === void 0 ? function () { return null; } : _b, rest = __rest(_a, ["children", "onClick"]);
    return (React.createElement(Container$b, __assign({}, rest, { onClick: onClick }),
        children,
        React.createElement(Overlay, null),
        React.createElement("i", null, "add_a_photo")));
};

var Container$a = styled('div', {
    borderRadius: 5,
    width: '100%',
    border: '1px solid $colors$primaryBorder',
    display: 'flex',
    '&:hover': {
        border: '1px solid $colors$secondaryBorder',
    },
    '&:focus-within': {
        border: '1px solid $colors$primaryFill',
    },
    variants: {
        small: {
            true: {
                flex: 'none',
                width: 80,
            },
        },
        disabled: {
            true: {
                cursor: 'not-allowed',
                '&:hover': {
                    border: '1px solid $colors$primaryBorder',
                },
                '&:focus-within': {
                    border: '1px solid $colors$primaryBorder',
                },
                '&>*': {
                    pointerEvents: 'none',
                },
            },
        },
    },
});
var Label$3 = styled('label', {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '$primaryBackground',
    color: '$secondaryFill',
    paddingLeft: '$space$4',
    paddingRight: '$space$5',
    opacity: 0.5,
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
});
var Input$2 = styled('input', {
    backgroundColor: 'transparent',
    border: 'none',
    color: '$primaryFill',
    width: '100%',
    height: 40,
    padding: 8,
    '&::placeholder': {
        color: '$secondaryFill',
    },
    '&::-webkit-calendar-picker-indicator': {
        display: 'none',
    },
    '&:focus': {
        outline: 0,
    },
    variants: {
        disabled: {
            true: {
                pointerEvents: 'none',
                color: '$secondaryFill',
            },
        },
    },
});

var Input$1 = forwardRef(function (_a, ref) {
    var _b = _a.type, type = _b === void 0 ? 'text' : _b, placeholder = _a.placeholder, defaultValue = _a.defaultValue, value = _a.value, _c = _a.onChange, onChange = _c === void 0 ? function () { return null; } : _c, _d = _a.onBlur, onBlur = _d === void 0 ? function () { return null; } : _d, _e = _a.onFocus, onFocus = _e === void 0 ? function () { return null; } : _e, label = _a.label, _f = _a.disabled, disabled = _f === void 0 ? false : _f, _g = _a.small, small = _g === void 0 ? false : _g, rest = __rest(_a, ["type", "placeholder", "defaultValue", "value", "onChange", "onBlur", "onFocus", "label", "disabled", "small"]);
    var internalUID = useState(uniqueId('vinput-'))[0];
    return (React.createElement(Container$a, { small: small, disabled: disabled },
        label && React.createElement(Label$3, { htmlFor: internalUID }, label),
        React.createElement(Input$2, __assign({ id: internalUID, ref: ref, type: type, placeholder: placeholder, defaultValue: defaultValue, value: value, tabIndex: disabled ? -1 : 0, onChange: function (e) { return onChange(e.target.value); }, onFocus: onFocus, onBlur: onBlur, disabled: disabled }, rest))));
});
Input$1.displayName = 'designSystemInput';

var SVG = styled('svg', {
    filter: 'drop-shadow(0 0 0.75px rgba(0,0,0,.42)) drop-shadow(0 1px 0.5px rgba(0,0,0,.018)) drop-shadow(0 2px 3px rgba(0,0,0,.2))',
});
var mainOpacity = 0.7;
var secondaryOpacity = 0.05;
var LabelGIF = function () {
    return (React.createElement(SVG, { height: "20", viewBox: "0 0 38 20", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M4 0C1.79086 0 0 1.79086 0 4V16C0 18.2091 1.79086 20 4 20H34C36.2091 20 38 18.2091 38 16V4C38 1.79086 36.2091 0 34 0H4ZM13.8613 10.8418V9.39453H17.5996V12.8164C17.2363 13.168 16.709 13.4785 16.0176 13.748C15.3301 14.0137 14.6328 14.1465 13.9258 14.1465C13.0273 14.1465 12.2441 13.959 11.5762 13.584C10.9082 13.2051 10.4062 12.666 10.0703 11.9668C9.73438 11.2637 9.56641 10.5 9.56641 9.67578C9.56641 8.78125 9.75391 7.98633 10.1289 7.29102C10.5039 6.5957 11.0527 6.0625 11.7754 5.69141C12.3262 5.40625 13.0117 5.26367 13.832 5.26367C14.8984 5.26367 15.7305 5.48828 16.3281 5.9375C16.9297 6.38281 17.3164 7 17.4883 7.78906L15.7656 8.11133C15.6445 7.68945 15.416 7.35742 15.0801 7.11523C14.748 6.86914 14.332 6.74609 13.832 6.74609C13.0742 6.74609 12.4707 6.98633 12.0215 7.4668C11.5762 7.94727 11.3535 8.66016 11.3535 9.60547C11.3535 10.625 11.5801 11.3906 12.0332 11.9023C12.4863 12.4102 13.0801 12.6641 13.8145 12.6641C14.1777 12.6641 14.541 12.5938 14.9043 12.4531C15.2715 12.3086 15.5859 12.1348 15.8477 11.9316V10.8418H13.8613ZM19.1523 14V5.41016H20.8867V14H19.1523ZM22.5566 5.41016V14H24.291V10.3496H27.877V8.89648H24.291V6.86328H28.4453V5.41016H22.5566Z", fill: "white", fillOpacity: mainOpacity }),
        React.createElement("path", { d: "M13.8613 10.8418V9.39453H17.5996V12.8164C17.2363 13.168 16.709 13.4785 16.0176 13.748C15.3301 14.0137 14.6328 14.1465 13.9258 14.1465C13.0273 14.1465 12.2441 13.959 11.5762 13.584C10.9082 13.2051 10.4062 12.666 10.0703 11.9668C9.73438 11.2637 9.56641 10.5 9.56641 9.67578C9.56641 8.78125 9.75391 7.98633 10.1289 7.29102C10.5039 6.5957 11.0527 6.0625 11.7754 5.69141C12.3262 5.40625 13.0117 5.26367 13.832 5.26367C14.8984 5.26367 15.7305 5.48828 16.3281 5.9375C16.9297 6.38281 17.3164 7 17.4883 7.78906L15.7656 8.11133C15.6445 7.68945 15.416 7.35742 15.0801 7.11523C14.748 6.86914 14.332 6.74609 13.832 6.74609C13.0742 6.74609 12.4707 6.98633 12.0215 7.4668C11.5762 7.94727 11.3535 8.66016 11.3535 9.60547C11.3535 10.625 11.5801 11.3906 12.0332 11.9023C12.4863 12.4102 13.0801 12.6641 13.8145 12.6641C14.1777 12.6641 14.541 12.5938 14.9043 12.4531C15.2715 12.3086 15.5859 12.1348 15.8477 11.9316V10.8418H13.8613Z", fill: "white", fillOpacity: secondaryOpacity }),
        React.createElement("path", { d: "M19.1523 14V5.41016H20.8867V14H19.1523Z", fill: "white", fillOpacity: secondaryOpacity }),
        React.createElement("path", { d: "M22.5566 14V5.41016H28.4453V6.86328H24.291V8.89648H27.877V10.3496H24.291V14H22.5566Z", fill: "white", fillOpacity: secondaryOpacity })));
};
var LabelWEB = function () {
    return (React.createElement(SVG, { height: "20", viewBox: "0 0 38 20", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M4 0C1.79086 0 0 1.79086 0 4V16C0 18.2091 1.79086 20 4 20H34C36.2091 20 38 18.2091 38 16V4C38 1.79086 36.2091 0 34 0H4ZM7.05078 14.5898L5 6H6.77539L8.07031 11.9004L9.64062 6H11.7031L13.209 12L14.5273 6H16.2734L14.1875 14.5898H12.3477L10.6367 8.16797L8.93164 14.5898H7.05078ZM17.1641 14.5898V6H23.5332V7.45312H18.8984V9.35742H23.2109V10.8047H18.8984V13.1426H23.6973V14.5898H17.1641ZM28.6074 6H25.1738V14.5898H28.0977C29.2031 14.582 29.9004 14.5625 30.1895 14.5312C30.6504 14.4805 31.0371 14.3457 31.3496 14.127C31.666 13.9043 31.9141 13.6094 32.0938 13.2422C32.2773 12.8711 32.3691 12.4902 32.3691 12.0996C32.3691 11.6035 32.2285 11.1719 31.9473 10.8047C31.666 10.4375 31.2637 10.1777 30.7402 10.0254C31.1113 9.85742 31.4043 9.60547 31.6191 9.26953C31.8379 8.93359 31.9473 8.56445 31.9473 8.16211C31.9473 7.79102 31.8594 7.45703 31.6836 7.16016C31.5078 6.85938 31.2871 6.61914 31.0215 6.43945C30.7598 6.25977 30.4609 6.14258 30.125 6.08789C29.793 6.0293 29.2871 6 28.6074 6ZM26.9082 9.41602V7.42969H27.9043C28.7207 7.42969 29.2148 7.43945 29.3867 7.45898C29.6758 7.49414 29.8926 7.5957 30.0371 7.76367C30.1855 7.92773 30.2598 8.14258 30.2598 8.4082C30.2598 8.68555 30.1738 8.91016 30.002 9.08203C29.834 9.25 29.6016 9.35156 29.3047 9.38672C29.1406 9.40625 28.7207 9.41602 28.0449 9.41602H26.9082ZM26.9082 13.1426V10.8457H28.3086C29.0977 10.8457 29.6094 10.8867 29.8438 10.9688C30.082 11.0508 30.2637 11.1816 30.3887 11.3613C30.5137 11.541 30.5762 11.7598 30.5762 12.0176C30.5762 12.3223 30.4941 12.5664 30.3301 12.75C30.1699 12.9297 29.9609 13.043 29.7031 13.0898C29.5352 13.125 29.1387 13.1426 28.5137 13.1426H26.9082Z", fill: "white", fillOpacity: mainOpacity }),
        React.createElement("path", { d: "M7.05078 14.5898L5 6H6.77539L8.07031 11.9004L9.64062 6H11.7031L13.209 12L14.5273 6H16.2734L14.1875 14.5898H12.3477L10.6367 8.16797L8.93164 14.5898H7.05078Z", fill: "white", fillOpacity: secondaryOpacity }),
        React.createElement("path", { d: "M17.1641 14.5898V6H23.5332V7.45312H18.8984V9.35742H23.2109V10.8047H18.8984V13.1426H23.6973V14.5898H17.1641Z", fill: "white", fillOpacity: secondaryOpacity }),
        React.createElement("path", { d: "M25.1738 6H28.6074C29.2871 6 29.793 6.0293 30.125 6.08789C30.4609 6.14258 30.7598 6.25977 31.0215 6.43945C31.2871 6.61914 31.5078 6.85938 31.6836 7.16016C31.8594 7.45703 31.9473 7.79102 31.9473 8.16211C31.9473 8.56445 31.8379 8.93359 31.6191 9.26953C31.4043 9.60547 31.1113 9.85742 30.7402 10.0254C31.2637 10.1777 31.666 10.4375 31.9473 10.8047C32.2285 11.1719 32.3691 11.6035 32.3691 12.0996C32.3691 12.4902 32.2773 12.8711 32.0938 13.2422C31.9141 13.6094 31.666 13.9043 31.3496 14.127C31.0371 14.3457 30.6504 14.4805 30.1895 14.5312C29.9004 14.5625 29.2031 14.582 28.0977 14.5898H25.1738V6ZM26.9082 7.42969V9.41602H28.0449C28.7207 9.41602 29.1406 9.40625 29.3047 9.38672C29.6016 9.35156 29.834 9.25 30.002 9.08203C30.1738 8.91016 30.2598 8.68555 30.2598 8.4082C30.2598 8.14258 30.1855 7.92773 30.0371 7.76367C29.8926 7.5957 29.6758 7.49414 29.3867 7.45898C29.2148 7.43945 28.7207 7.42969 27.9043 7.42969H26.9082ZM26.9082 10.8457V13.1426H28.5137C29.1387 13.1426 29.5352 13.125 29.7031 13.0898C29.9609 13.043 30.1699 12.9297 30.3301 12.75C30.4941 12.5664 30.5762 12.3223 30.5762 12.0176C30.5762 11.7598 30.5137 11.541 30.3887 11.3613C30.2637 11.1816 30.082 11.0508 29.8438 10.9688C29.6094 10.8867 29.0977 10.8457 28.3086 10.8457H26.9082Z", fill: "white", fillOpacity: secondaryOpacity })));
};
var LabelVIDEO = function () {
    return (React.createElement(SVG, { height: "20", viewBox: "0 0 48 48" },
        React.createElement("path", { d: "M9.6 46.5c-1 0-2-.3-2.9-.8-1.8-1.1-2.9-2.9-2.9-5.1V7.3c0-2.1 1.1-4 2.9-5.1 1.9-1.1 4.1-1.1 5.9 0l30.1 17.6c1.5.9 2.3 2.4 2.3 4.1 0 1.7-.9 3.2-2.3 4.1L12.6 45.7c-.9.5-2 .8-3 .8z", fill: "white", fillOpacity: mainOpacity })));
};
var LabelAUDIO = function () {
    return (React.createElement(SVG, { height: "20", viewBox: "0 0 48 48", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("path", { d: "M0 0h48v48h-48z", fill: "none" }),
        React.createElement("path", { d: "M14 36h4v-24h-4v24zm8 8h4v-40h-4v40zm-16-16h4v-8h-4v8zm24 8h4v-24h-4v24zm8-16v8h4v-8h-4z", fill: "white", fillOpacity: mainOpacity })));
};
var Label3D = function () {
    return (React.createElement(SVG, { height: "20", viewBox: "0 0 26 29", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement("path", { d: "M13 28L1 21.2V7.7L13 1L25 7.7V21.2L13 28ZM13 28V15M1 8.2L13 15M13 15L25 8.2", stroke: "white", strokeOpacity: mainOpacity, strokeWidth: "2", strokeMiterlimit: "10", strokeLinecap: "round", strokeLinejoin: "round" })));
};
var LabelPDF = function () {
    return (React.createElement(SVG, { viewBox: "0 0 24 24", height: "20" },
        React.createElement("polygon", { points: "12,3 1,9 12,15 23,9 ", fill: "#ffffff", opacity: mainOpacity }),
        React.createElement("polygon", { points: "19,12.8 12,17 5,12.8 5,17.2 12,21 19,17.2 ", fill: "#ffffff", opacity: mainOpacity })));
};

var Path = styled('path', {
    fill: '$primaryFill',
});

var Logo = function () {
    return (React.createElement("svg", { width: "60", height: "10", viewBox: "0 0 60 10", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
        React.createElement(Path, { d: "M4.608 7.36804L7.104 0.184039H9.04L5.68 9.00004H3.504L0.16 0.184039H2.112L4.608 7.36804Z" }),
        React.createElement(Path, { d: "M18.4988 4.37604C18.4988 4.70671 18.4774 5.00537 18.4347 5.27204H11.6987C11.7521 5.97604 12.0134 6.54137 12.4828 6.96804C12.9521 7.39471 13.5281 7.60804 14.2107 7.60804C15.1921 7.60804 15.8854 7.19737 16.2908 6.37604H18.2587C17.9921 7.18671 17.5067 7.85337 16.8027 8.37604C16.1094 8.88804 15.2454 9.14404 14.2107 9.14404C13.3681 9.14404 12.6108 8.95737 11.9388 8.58404C11.2774 8.20004 10.7547 7.66671 10.3707 6.98404C9.99742 6.29071 9.81075 5.49071 9.81075 4.58404C9.81075 3.67737 9.99208 2.88271 10.3547 2.20004C10.7281 1.50671 11.2454 0.973372 11.9067 0.600039C12.5787 0.226706 13.3467 0.0400391 14.2107 0.0400391C15.0427 0.0400391 15.7841 0.221372 16.4347 0.584039C17.0854 0.946705 17.5921 1.45871 17.9548 2.12004C18.3174 2.77071 18.4988 3.52271 18.4988 4.37604ZM16.5947 3.80004C16.5841 3.12804 16.3441 2.58937 15.8748 2.18404C15.4054 1.77871 14.8241 1.57604 14.1307 1.57604C13.5014 1.57604 12.9628 1.77871 12.5148 2.18404C12.0668 2.57871 11.8001 3.11737 11.7148 3.80004H16.5947Z" }),
        React.createElement(Path, { d: "M22.1178 1.46404C22.3844 1.01604 22.7364 0.669372 23.1738 0.424039C23.6218 0.168039 24.1497 0.0400391 24.7577 0.0400391V1.92804H24.2938C23.5791 1.92804 23.0351 2.10937 22.6618 2.47204C22.2991 2.83471 22.1178 3.46404 22.1178 4.36004V9.00004H20.2938V0.184039H22.1178V1.46404Z" }),
        React.createElement(Path, { d: "M29.6344 9.14404C28.941 9.14404 28.317 9.02137 27.7624 8.77604C27.2184 8.52004 26.7864 8.17871 26.4664 7.75204C26.1464 7.31471 25.9757 6.82937 25.9544 6.29604H27.8424C27.8744 6.66937 28.0504 6.98404 28.3704 7.24004C28.701 7.48537 29.1117 7.60804 29.6024 7.60804C30.1144 7.60804 30.509 7.51204 30.7864 7.32004C31.0744 7.11737 31.2184 6.86137 31.2184 6.55204C31.2184 6.22137 31.0584 5.97604 30.7384 5.81604C30.429 5.65604 29.933 5.48004 29.2504 5.28804C28.589 5.10671 28.0504 4.93071 27.6344 4.76004C27.2184 4.58937 26.8557 4.32804 26.5464 3.97604C26.2477 3.62404 26.0984 3.16004 26.0984 2.58404C26.0984 2.11471 26.237 1.68804 26.5144 1.30404C26.7917 0.909372 27.1864 0.600039 27.6984 0.376039C28.221 0.152039 28.8184 0.0400391 29.4904 0.0400391C30.493 0.0400391 31.2984 0.296039 31.9064 0.808039C32.525 1.30937 32.8557 1.99737 32.8984 2.87204H31.0744C31.0424 2.47737 30.8824 2.16271 30.5944 1.92804C30.3064 1.69337 29.917 1.57604 29.4264 1.57604C28.9464 1.57604 28.5784 1.66671 28.3224 1.84804C28.0664 2.02937 27.9384 2.26937 27.9384 2.56804C27.9384 2.80271 28.0237 3.00004 28.1944 3.16004C28.365 3.32004 28.573 3.44804 28.8184 3.54404C29.0637 3.62937 29.4264 3.74137 29.9064 3.88004C30.5464 4.05071 31.069 4.22671 31.4744 4.40804C31.8904 4.57871 32.2477 4.83471 32.5464 5.17604C32.845 5.51737 32.9997 5.97071 33.0104 6.53604C33.0104 7.03737 32.8717 7.48537 32.5944 7.88004C32.317 8.27471 31.9224 8.58404 31.4104 8.80804C30.909 9.03204 30.317 9.14404 29.6344 9.14404Z" }),
        React.createElement(Path, { d: "M42.9652 0.184039V9.00004H41.1413V7.96004C40.8533 8.32271 40.4746 8.61071 40.0052 8.82404C39.5466 9.02671 39.0559 9.12804 38.5332 9.12804C37.8399 9.12804 37.2159 8.98404 36.6613 8.69604C36.1173 8.40804 35.6852 7.98137 35.3652 7.41604C35.0559 6.85071 34.9012 6.16804 34.9012 5.36804V0.184039H36.7093V5.09604C36.7093 5.88537 36.9066 6.49337 37.3013 6.92004C37.6959 7.33604 38.2346 7.54404 38.9173 7.54404C39.5999 7.54404 40.1386 7.33604 40.5332 6.92004C40.9386 6.49337 41.1413 5.88537 41.1413 5.09604V0.184039H42.9652Z" }),
        React.createElement(Path, { d: "M56.0439 0.0400391C56.7372 0.0400391 57.3559 0.184039 57.8999 0.472038C58.4545 0.760038 58.8865 1.18671 59.1959 1.75204C59.5159 2.31737 59.6759 3.00004 59.6759 3.80004V9.00004H57.8679V4.07204C57.8679 3.28271 57.6705 2.68004 57.2759 2.26404C56.8812 1.83737 56.3425 1.62404 55.6599 1.62404C54.9772 1.62404 54.4332 1.83737 54.0279 2.26404C53.6332 2.68004 53.4359 3.28271 53.4359 4.07204V9.00004H51.6279V4.07204C51.6279 3.28271 51.4305 2.68004 51.0359 2.26404C50.6412 1.83737 50.1025 1.62404 49.4199 1.62404C48.7372 1.62404 48.1932 1.83737 47.7879 2.26404C47.3932 2.68004 47.1959 3.28271 47.1959 4.07204V9.00004H45.3719V0.184039H47.1959V1.19204C47.4945 0.829372 47.8732 0.546706 48.3319 0.344039C48.7905 0.141372 49.2812 0.0400391 49.8039 0.0400391C50.5079 0.0400391 51.1372 0.189372 51.6919 0.488039C52.2465 0.786706 52.6732 1.21871 52.9719 1.78404C53.2385 1.25071 53.6545 0.829372 54.2199 0.520039C54.7852 0.200039 55.3932 0.0400391 56.0439 0.0400391Z" })));
};
function Monogram() {
    return (React.createElement("svg", { width: "24", viewBox: "0 0 850 799", fill: "none" },
        React.createElement(Path, { d: "M798.15 168.299C783.276 141.524 764.576 116.024 742.901 91.7992L461.128 373.572L742.901 655.77C764.576 631.545 783.276 605.621 798.15 578.846L593.302 373.572L798.15 168.299Z" }),
        React.createElement(Path, { d: "M706.776 55.6745C682.551 33.5747 657.052 15.2999 630.277 4.17233e-07L425.004 205.273L219.73 4.17233e-07C192.53 15.2999 167.03 33.5747 142.805 55.6745L425.004 337.447L706.776 55.6745Z" }),
        React.createElement(Path, { d: "M847.45 326.822H712.301L665.552 373.572L712.301 420.322H847.45C849.15 405.022 850 389.297 850 373.572C850 357.422 849.15 342.122 847.45 326.822Z" }),
        React.createElement(Path, { d: "M0.00374997 373.574C-0.421246 490.874 41.6534 591.173 124.528 674.048C207.827 757.347 307.701 798.997 425.001 798.572C533.8 798.997 627.724 763.297 707.198 691.898L107.103 91.802C35.2784 170.851 -0.421246 264.775 0.00374997 373.574Z" })));
}

var Container$9 = styled('span', {
    display: 'flex',
    marginBottom: '$space$3',
});
var Root = styled('div', {
    display: 'flex',
    backgroundColor: '$secondaryBackground',
    borderRadius: 5,
    padding: 2,
});
var Item$1 = styled('div', {
    all: 'unset',
    borderRadius: 5,
    padding: '$space$5 $space$2',
    backgroundColor: '$secondaryBackground',
    color: '$secondaryFill',
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
    '&:focus': { outline: 0 },
    '&:hover': { color: '$primaryFill' },
    '&[data-state="checked"]': {
        backgroundColor: '$primaryBackground',
        color: '$primaryFill',
    },
});

var RadioGroup = function (_a) {
    var value = _a.value, onChange = _a.onChange, data = _a.data;
    var _b = useState((value || data[0].key)), focused = _b[0], setValue = _b[1];
    var handleValueChange = function (key) {
        setValue(key);
        if (onChange) {
            onChange(key);
        }
    };
    return (React.createElement(Container$9, null,
        React.createElement(RadioGroupPrimitive.Root, { defaultValue: focused, onValueChange: handleValueChange, asChild: true },
            React.createElement(Root, null, data.map(function (item) {
                return (React.createElement(RadioGroupPrimitive.Item, { key: item.label, value: item.key, asChild: true },
                    React.createElement(Item$1, null, item.label)));
            })))));
};

var Container$8 = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    borderBottom: '1px solid $colors$primaryBorder',
    variants: {
        disabled: {
            true: {
                opacity: 0.25,
                cursor: 'not-allowed',
                '&>*': {
                    pointerEvents: 'none',
                },
            },
        },
    },
});
var Label$2 = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
        margin: 0,
        fontSize: 16,
    },
    h6: {
        marginBottom: '$space$4',
    },
    p: {
        color: '$secondaryFill',
    },
});
var Item = styled('div', {
    display: 'flex',
    alignItems: 'center',
    marginTop: '$space$3',
    '@bp1': {
        marginTop: 0,
    },
});

var Row = function (_a) {
    var title = _a.title, label = _a.label, extra = _a.extra, children = _a.children, _b = _a.disabled, disabled = _b === void 0 ? false : _b;
    return (React.createElement(Container$8, { disabled: disabled },
        React.createElement(Box, { css: {
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                flexDirection: 'column',
                '@bp1': {
                    flexDirection: 'row',
                },
            } },
            React.createElement(Label$2, null,
                React.createElement("h6", null, title),
                label),
            React.createElement(Item, null, children)),
        extra && React.createElement(Box, { css: { paddingTop: '$space$3' } }, extra)));
};

var spin = keyframes({
    '0%': {
        transform: 'translateX(-50%) translateY(-50%) rotate(0deg)',
    },
    '100%': {
        transform: 'translateX(-50%) translateY(-50%) rotate(360deg)',
    },
});
var Container$7 = styled('div', {
    position: 'relative',
    borderRadius: 9999,
    width: 20,
    height: 20,
    border: '2px solid $colors$primaryBorder',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'border-color 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out',
    '&:before': {
        content: "''",
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 0,
        height: 0,
        borderRadius: 9999,
        transform: 'translateX(-50%) translateY(-50%)',
        backgroundColor: 'transparent',
        transition: 'all 0.2s ease-out',
    },
    '&:after': {
        content: "''",
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 16,
        height: 16,
        borderRadius: 9999,
        opacity: 0,
        transform: 'translateX(-50%) translateY(-50%)',
        border: '2px solid transparent',
        borderTop: '2px solid $colors$primaryFill',
        transition: 'opacity 0.3s ease-in-out',
        animation: "".concat(spin, " 1s linear infinite"),
    },
    i: {
        position: 'relative',
        fontSize: 16,
        fontWeight: 700,
        color: 'white',
        opacity: 0,
        transition: 'all 0.3s ease-in-out',
    },
    variants: {
        progress: {
            true: {
                '&:after': {
                    opacity: 1,
                },
            },
        },
        error: {
            true: {
                border: '2px solid $colors$red',
                i: {
                    opacity: 1,
                    transition: 'all 0.3s 0.2s ease-in-out',
                },
                '&:before': {
                    width: 18,
                    height: 18,
                    backgroundColor: '$red',
                },
            },
        },
        success: {
            true: {
                border: '2px solid $colors$green',
                i: {
                    opacity: 1,
                    transition: 'all 0.3s 0.2s ease-in-out',
                },
                '&:before': {
                    width: 18,
                    height: 18,
                    backgroundColor: '$green',
                },
            },
        },
        small: {
            true: {
                width: 16,
                height: 16,
                '&:after': {
                    content: "''",
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: 12,
                    height: 12,
                },
            },
        },
    },
});

var Step = function (_a) {
    var _b = _a.progress, progress = _b === void 0 ? false : _b, _c = _a.success, success = _c === void 0 ? false : _c, _d = _a.error, error = _d === void 0 ? false : _d, _e = _a.small, small = _e === void 0 ? false : _e;
    return (React.createElement(Container$7, { progress: progress, success: success, error: error, small: small },
        React.createElement("i", null,
            success && 'check',
            error && 'close')));
};

var Container$6 = styled('div', {
    display: 'flex',
    borderRadius: 5,
    width: '100%',
    border: '1px solid $colors$primaryBorder',
    alignItems: 'center',
    '&:hover': {
        border: '1px solid $colors$secondaryBorder',
    },
    '&:focus-within': {
        border: '1px solid $colors$primaryFill',
    },
    variants: {
        disabled: {
            true: {
                cursor: 'not-allowed',
                '&:hover': {
                    border: '1px solid $colors$primaryBorder',
                },
                '&:focus-within': {
                    border: '1px solid $colors$primaryBorder',
                },
                '&>*': {
                    pointerEvents: 'none',
                },
            },
        },
        subtle: {
            true: {
                backgroundColor: '$secondaryBackground',
                border: '1px solid transparent',
            },
        },
    },
});
var Label$1 = styled('label', {
    display: 'flex',
    alignItems: 'center',
    padding: '$space$4',
    paddingRight: 'unset',
    i: {
        color: '$secondaryFill',
    },
});
var Input = styled('input', {
    backgroundColor: 'transparent',
    border: 'none',
    color: '$primaryFill',
    width: '100%',
    padding: '$space$4',
    fontSize: 16,
    zIndex: 0,
    '&::placeholder': {
        color: '$secondaryFill',
    },
    '&:focus': {
        outline: 0,
    },
    '@bp1': {
        fontSize: 14,
    },
    variants: {
        disabled: {
            true: {
                pointerEvents: 'none',
                color: '$secondaryFill',
            },
        },
    },
});

var Search = forwardRef(function (_a, ref) {
    var placeholder = _a.placeholder, defaultValue = _a.defaultValue, value = _a.value, _b = _a.onChange, onChange = _b === void 0 ? function () { return null; } : _b, _c = _a.onFocus, onFocus = _c === void 0 ? function () { return null; } : _c, _d = _a.onBlur, onBlur = _d === void 0 ? function () { return null; } : _d, _e = _a.disabled, disabled = _e === void 0 ? false : _e, _f = _a.subtle, subtle = _f === void 0 ? false : _f, _g = _a.progress, progress = _g === void 0 ? false : _g, rest = __rest(_a, ["placeholder", "defaultValue", "value", "onChange", "onFocus", "onBlur", "disabled", "subtle", "progress"]);
    var internalUID = useState(uniqueId('vsearch-'))[0];
    return (React.createElement(Container$6, { disabled: disabled, subtle: subtle },
        React.createElement(Label$1, { htmlFor: internalUID, css: { i: { fontSize: 20 } } },
            React.createElement("i", null, "search")),
        React.createElement(Input, __assign({ id: internalUID, ref: ref, type: "text", placeholder: placeholder, defaultValue: defaultValue, value: value, tabIndex: disabled ? -1 : 0, onChange: function (e) { return onChange(e.target.value); }, onFocus: onFocus, onBlur: onBlur, disabled: disabled, autoComplete: "off" }, rest)),
        progress && (React.createElement(Box, { css: { paddingRight: '$space$4' }, vertical: true },
            React.createElement(Step, { progress: true, small: true })))));
});
Search.displayName = 'designSystemSearch';

var _a$1;
var I = styled('i', {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 14,
    color: '$secondaryFill',
});
var Icon = styled(I, {
    left: 8,
});
var Arrow = styled(I, {
    right: 8,
});
var Container$5 = styled('div', {
    position: 'relative',
    borderRadius: 5,
    width: '100%',
    border: '1px solid $colors$primaryBorder',
    '&:hover': {
        border: '1px solid $colors$secondaryBorder',
    },
    '&:focus-within': {
        border: '1px solid $colors$primaryFill',
    },
    variants: {
        variant: {
            basic: (_a$1 = {
                    border: 'none',
                    '&:hover': {
                        border: 'none',
                    },
                    '&:focus-within': {
                        border: 'none',
                    }
                },
                _a$1["& ".concat(Icon)] = {
                    display: 'none',
                },
                _a$1["& ".concat(Arrow)] = {
                    display: 'none',
                },
                _a$1),
            market: {},
        },
    },
});
var Select = styled('select', {
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    appearance: 'none',
    color: '$secondaryFill',
    fontFamily: '$DMSans',
    fontSize: 16,
    textRendering: 'auto',
    width: '100%',
    height: 40,
    paddingLeft: 8,
    paddingRight: 28,
    cursor: 'pointer',
    variants: {
        hasIcon: {
            true: {
                paddingLeft: 28,
            },
        },
        variant: {
            basic: {
                paddingRight: 8,
                '&:hover': {
                    color: '$primaryFill',
                },
                '&:focus-within': {
                    color: '$primaryFill',
                },
            },
            market: {
                fontSize: 16,
                height: 32,
                '@bp1': {
                    fontSize: 14,
                },
            },
        },
    },
});

var Variants;
(function (Variants) {
    Variants["basic"] = "basic";
    Variants["market"] = "market";
})(Variants || (Variants = {}));
var Selector = function (_a) {
    var label = _a.label, variant = _a.variant, value = _a.value, list = _a.list, onChange = _a.onChange;
    var handleChange = function (e) {
        var nextValue = e.target.value;
        onChange(nextValue);
    };
    var getIcon = function () {
        var found = list.find(function (e) { return e.value === value; });
        if (found === null || found === void 0 ? void 0 : found.icon) {
            return found.icon;
        }
        return null;
    };
    var icon = getIcon();
    return (React.createElement(Container$5, { variant: variant },
        icon && React.createElement(Icon, null, icon),
        React.createElement(Select, { variant: variant, value: value, onChange: handleChange, hasIcon: !!icon }, list.map(function (item) { return (React.createElement("option", { key: item.value, value: item.value }, item.labelForDefault ? label : item.label)); })),
        React.createElement(Arrow, null, "unfold_more")));
};

var pulsing = keyframes({
    '0%': { opacity: 1 },
    '50%': { opacity: 0.5 },
    '100%': { opacity: 1 },
});
var Status = styled('div', {
    width: 6,
    height: 6,
    margin: '0 $space$4',
    borderRadius: 9999,
    animation: "".concat(pulsing, " 2s linear infinite"),
    variants: {
        variant: {
            success: {
                backgroundColor: '$green',
            },
            warning: {
                backgroundColor: '$yellow',
            },
            error: {
                backgroundColor: '$red',
            },
        },
    },
    defaultVariants: {
        variant: 'success',
    },
});

var Container$4 = styled('button', {
    backgroundColor: 'transparent',
    border: 'none',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    p: {
        fontSize: 13,
        textAlign: 'left',
        fontWeight: 700,
        margin: 0,
        color: '$secondaryFill',
    },
    i: {
        width: 18,
        height: 18,
        fontSize: 16,
        color: '$secondaryFill',
        marginLeft: '$space$5',
    },
    '&:focus': {
        outline: 'none',
        p: {
            color: '$primaryFill',
        },
        i: {
            color: '$primaryFill',
        },
    },
    '&:hover': {
        p: {
            color: '$primaryFill',
        },
        i: {
            color: '$primaryFill',
        },
    },
});

var Label = function (_a) {
    var label = _a.label, _b = _a.onClick, onClick = _b === void 0 ? function () { return null; } : _b, _c = _a.order, order = _c === void 0 ? 0 : _c;
    return (React.createElement(Container$4, { onClick: onClick },
        React.createElement(Text, { unset: true }, label),
        React.createElement(Box, { css: { marginLeft: '$space$4' } },
            order === 0 && React.createElement("i", null, "\u00A0"),
            order === 1 && React.createElement("i", null, "arrow_drop_down"),
            order === 2 && React.createElement("i", null, "arrow_drop_up"))));
};

var Container$3 = styled('div', {
    border: '1px solid $colors$primaryBorder',
    borderRadius: 5,
    overflow: 'auto',
});
var Table$1 = styled('table', {
    width: '100%',
    borderCollapse: 'collapse',
});
var Thead = styled('thead', {
    // backgroundColor: '$secondaryBackground',
    th: {
        height: 50,
        whiteSpace: 'nowrap',
        padding: '0 $space$4',
    },
});
var Tbody = styled('tbody', {
    tr: {
        height: 50,
        whiteSpace: 'nowrap',
        borderTop: '1px solid $colors$primaryBorder',
        // '&:nth-child(even)': {
        //   backgroundColor: '$secondaryBackground',
        // },
    },
    td: {
        whiteSpace: 'nowrap',
        padding: '0 $space$4',
    },
});

var Table = function (_a) {
    var settings = _a.settings, data = _a.data, _b = _a.canSelect, canSelect = _b === void 0 ? true : _b, _c = _a.batch, batch = _c === void 0 ? [] : _c;
    // we need to store settings and data locally because we manipulate it to store "state" per row
    // to define things like sorting and selection.
    var _d = useState(settings), parsedSettings = _d[0], setParsedSettings = _d[1];
    var _e = useState(data), parsedData = _e[0], setParsedData = _e[1];
    // update if new settings/data arrives
    useEffect(function () {
        setParsedSettings(settings);
        setParsedData(data);
    }, [settings, data]);
    var handleSortBy = function (key) {
        // settings
        var prevSettings = __spreadArray([], parsedSettings, true);
        var nextSettings = prevSettings.map(function (setting) {
            if (setting.key === key) {
                if (setting.order === undefined) {
                    setting.order = 0;
                }
                setting.order += 1;
                setting.order %= 3;
            }
            else {
                delete setting.order;
            }
            return setting;
        });
        // data
        var found = nextSettings.find(function (e) { return e.key === key; });
        var requiresSorting = (found === null || found === void 0 ? void 0 : found.order) !== 0;
        if (requiresSorting) {
            // sort
            var prevData = __spreadArray([], parsedData, true);
            var nextData = prevData.sort(function (a, b) {
                if ((found === null || found === void 0 ? void 0 : found.order) === 1) {
                    return a[key] > b[key] ? 1 : -1;
                }
                return a[key] > b[key] ? -1 : 1;
            });
            // set settings and data
            setParsedSettings(nextSettings);
            setParsedData(nextData);
        }
        else {
            // set settings and data
            setParsedData(data);
        }
    };
    var handleSelectionAll = function (value) {
        var nextData = parsedData.map(function (row) {
            row.selected = value;
            return row;
        });
        setParsedData(nextData);
    };
    var handleSelection = function (index, value) {
        var nextData = __spreadArray([], parsedData, true);
        nextData[index].selected = value;
        setParsedData(nextData);
    };
    var hasSelection = function () {
        return parsedData.filter(function (s) { return s.selected; }).length > 0;
    };
    var displayRowSelection = function () {
        var count = parsedData.filter(function (s) { return s.selected; }).length;
        var total = parsedData.length;
        if (count) {
            if (count === total) {
                return 'All rows selected';
            }
            if (count > 1) {
                return "".concat(count, " rows selected");
            }
            return "".concat(count, " row selected");
        }
        return null;
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Container$3, null,
            React.createElement(Table$1, null,
                React.createElement(Thead, null,
                    React.createElement("tr", null,
                        canSelect && (React.createElement("th", null,
                            React.createElement(Box, { css: {
                                    width: 30,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                } },
                                React.createElement(Checkbox, { onChange: handleSelectionAll })))),
                        parsedSettings.map(function (setting) {
                            return (React.createElement("th", { key: setting.key },
                                React.createElement(Label, { order: setting.order, label: setting.label, onClick: function () { return handleSortBy(setting.key); } })));
                        }))),
                React.createElement(Tbody, null, parsedData.map(function (row, rowIndex) {
                    return (React.createElement("tr", { key: "row".concat(rowIndex) },
                        canSelect && (React.createElement("td", null,
                            React.createElement(Box, { css: {
                                    width: 30,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                } },
                                React.createElement(Checkbox, { value: row.selected, onChange: function (value) { return handleSelection(rowIndex, value); } })))),
                        parsedSettings.map(function (component) {
                            var key = component.key, Component = component.Component;
                            return (React.createElement("td", { key: "row".concat(rowIndex, "-").concat(component.key) }, Component ? React.createElement(Component, __assign({}, row)) : React.createElement(Text, { unset: true }, row[key])));
                        })));
                })))),
        hasSelection() && (React.createElement(Box, { css: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '$space$3',
            } },
            React.createElement(Text, { unset: true, css: { color: '$secondaryFill', fontSize: 12 } }, displayRowSelection()),
            React.createElement(Box, null, batch.map(function (action) {
                return (React.createElement(Button$1, { key: action.label, variant: "market", onClick: function () {
                        if (action.fn) {
                            action.fn(parsedData.filter(function (e) { return e.selected; }));
                        }
                    } }, action.label));
            }))))));
};

var Tag$1 = styled('span', {
    display: 'inline-flex',
    backgroundColor: '$secondaryBackground',
    color: '$secondaryFill',
    padding: '$space$5 $space$4',
    borderRadius: 5,
    fontSize: 12,
    border: '1px solid $colors$primaryBorder',
    '&:focus-within': {
        border: '1px solid $colors$primaryFill',
    },
});

var Tag = function (_a) {
    var label = _a.label, onClose = _a.onClose, rest = __rest(_a, ["label", "onClose"]);
    return (React.createElement(Tag$1, __assign({}, rest),
        label,
        onClose && (React.createElement(Button$1, { variant: "basic", css: {
                padding: 0,
                width: 16,
                height: 16,
                borderRadius: 9999,
                backgroundColor: '$primaryBorder',
                marginLeft: '$space$4',
                i: { fontSize: 14 },
            }, onClick: function () { return onClose(label); } },
            React.createElement("i", null, "close")))));
};

var Container$2 = styled('div', {
    borderRadius: 5,
    width: '100%',
    border: '1px solid $colors$primaryBorder',
    '&:hover': {
        border: '1px solid $colors$secondaryBorder',
    },
    '&:focus-within': {
        border: '1px solid $colors$primaryFill',
    },
    variants: {
        small: {
            true: {
                flex: 'none',
                width: 80,
            },
        },
        disabled: {
            true: {
                cursor: 'not-allowed',
            },
        },
    },
});
var Textarea$1 = styled('textarea', {
    backgroundColor: 'transparent',
    border: 'none',
    color: '$primaryFill',
    width: '100%',
    maxWidth: '100%',
    minHeight: 100,
    maxHeight: 150,
    padding: 8,
    whiteSpace: 'pre-wrap',
    resize: 'none',
    '&::placeholder': {
        color: '$secondaryFill',
    },
    '&:focus': {
        outline: 0,
    },
    variants: {
        disabled: {
            true: {
                pointerEvents: 'none',
                color: '$secondaryFill',
                opacity: 0.3,
            },
        },
    },
});

var Textarea = forwardRef(function (_a, ref) {
    var placeholder = _a.placeholder, value = _a.value, defaultValue = _a.defaultValue, _b = _a.onChange, onChange = _b === void 0 ? function () { return null; } : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.small, small = _d === void 0 ? false : _d, rest = __rest(_a, ["placeholder", "value", "defaultValue", "onChange", "disabled", "small"]);
    return (React.createElement(Container$2, { small: small, disabled: disabled },
        React.createElement(Textarea$1, __assign({ ref: ref, placeholder: placeholder, defaultValue: defaultValue, value: value, tabIndex: disabled ? -1 : 0, onChange: function (e) { return onChange(e.target.value); }, disabled: disabled }, rest))));
});
Textarea.displayName = 'designSystemTextarea';

var Container$1 = styled('button', {
    appearance: 'none',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    background: '#666666',
    borderRadius: 12,
    height: 24,
    width: 42,
    position: 'relative',
    transition: 'background 0.2s ease',
    '&:after': {
        content: '""',
        background: 'white',
        borderRadius: '50%',
        height: 20,
        width: 20,
        position: 'absolute',
        top: 2,
        left: 0,
        transform: 'translateX(2px)',
        transition: 'transform 0.2s ease',
    },
    variants: {
        toggled: {
            true: {
                background: '$primaryHighlight',
                '&:after': {
                    transform: 'translateX(20px)',
                },
            },
        },
    },
});

var Toggle = function (_a) {
    var value = _a.value, onChange = _a.onChange;
    var rToggled = useRef(value);
    var _b = useState(value), toggled = _b[0], setToggled = _b[1];
    useEffect(function () {
        if (value !== undefined) {
            if (rToggled.current !== value) {
                rToggled.current = value;
                setToggled(value);
            }
        }
    }, [value]);
    var onToggleClick = function () {
        rToggled.current = !toggled;
        setToggled(!toggled);
        onChange(!toggled);
    };
    return React.createElement(Container$1, { toggled: toggled, onClick: onToggleClick });
};

var scroll = keyframes({
    '0%': { transform: 'translateY(0)' },
    '30%': { transform: 'translateY(-210px)' },
    '100%': { transform: 'translateY(-210px)' },
});
var BrowserFrame = function (_a) {
    var children = _a.children;
    return (React.createElement(Box, { css: {
            flexDirection: 'column',
            border: '1px solid $colors$primaryBorder',
            borderRadius: 5,
            overflow: 'hidden',
            width: 300,
            height: 224,
        } },
        React.createElement(Box, null,
            React.createElement(Box, { css: { padding: '$space$4', '&>*': { marginRight: 4 } } },
                React.createElement(Box, { css: { width: 8, height: 8, borderRadius: 9999, backgroundColor: '$secondaryBorder' } }),
                React.createElement(Box, { css: { width: 8, height: 8, borderRadius: 9999, backgroundColor: '$secondaryBorder' } }),
                React.createElement(Box, { css: { width: 8, height: 8, borderRadius: 9999, backgroundColor: '$secondaryBorder' } }))),
        React.createElement(Box, { css: { width: 300, height: 200, overflow: 'hidden' } }, children)));
};
var Thumb = function (_a) {
    var height = _a.height, _b = _a.info, info = _b === void 0 ? false : _b, _c = _a.market, market = _c === void 0 ? false : _c, _d = _a.labels, labels = _d === void 0 ? false : _d;
    return (React.createElement(Box, { css: { position: 'relative', flexDirection: 'column' } },
        React.createElement(Box, { css: {
                position: 'relative',
                backgroundColor: '$primaryBackground',
                width: '100%',
                height: height,
            } }),
        labels && (React.createElement(Box, { css: {
                position: 'absolute',
                bottom: 2,
                right: 2,
                width: 6,
                height: 3,
                backgroundColor: '$primaryBorder',
            } })),
        React.createElement(Box, { css: {
                position: 'absolute',
                top: 'calc(100%)',
                left: 0,
                width: '100%',
                flexDirection: 'column',
            } },
            info && (React.createElement(React.Fragment, null,
                React.createElement(Box, { css: { backgroundColor: '$primaryBorder', marginLeft: 2, marginBottom: 1, width: height / 2, height: 2 } }),
                React.createElement(Box, { css: { backgroundColor: '$primaryBorder', marginLeft: 2, marginBottom: 1, width: height / 3, height: 2 } }))),
            market && (React.createElement(Box, { css: { justifyContent: 'space-between' } },
                React.createElement(Box, { css: { backgroundColor: '$secondaryBorder', marginLeft: 2, marginBottom: 1, width: 6, height: 2 } }),
                React.createElement(Box, { css: { backgroundColor: '$secondaryBorder', marginRight: 2, marginBottom: 1, width: 6, height: 2 } }))))));
};
var Masonry = function (_a) {
    var _b = _a.info, info = _b === void 0 ? false : _b, _c = _a.market, market = _c === void 0 ? false : _c, _d = _a.labels, labels = _d === void 0 ? false : _d;
    return (React.createElement(Box, { css: {
            columnCount: 5,
            columnGap: 10,
            width: '100%',
            animation: "".concat(scroll, " 3s ease-out infinite"),
        } },
        React.createElement(Box, { css: { width: '20%', flexDirection: 'column', '&>*': { marginBottom: 10 } } },
            React.createElement(Thumb, { height: 50, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 60, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 70, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 50, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 60, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 70, info: info, market: market, labels: labels })),
        React.createElement(Box, { css: { width: '20%', flexDirection: 'column', '&>*': { marginBottom: 10 } } },
            React.createElement(Thumb, { height: 40, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 50, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 40, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 90, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 50, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 40, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 40, info: info, market: market, labels: labels })),
        React.createElement(Box, { css: { width: '20%', flexDirection: 'column', '&>*': { marginBottom: 10 } } },
            React.createElement(Thumb, { height: 50, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 60, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 70, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 50, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 60, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 70, info: info, market: market, labels: labels })),
        React.createElement(Box, { css: { width: '20%', flexDirection: 'column', '&>*': { marginBottom: 10 } } },
            React.createElement(Thumb, { height: 60, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 50, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 40, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 90, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 50, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 40, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 20, info: info, market: market, labels: labels })),
        React.createElement(Box, { css: { width: '20%', flexDirection: 'column', '&>*': { marginBottom: 10 } } },
            React.createElement(Thumb, { height: 40, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 50, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 40, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 90, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 50, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 40, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 40, info: info, market: market, labels: labels }))));
};
var Grid = function (_a) {
    var _b = _a.info, info = _b === void 0 ? false : _b, _c = _a.market, market = _c === void 0 ? false : _c, _d = _a.labels, labels = _d === void 0 ? false : _d;
    var v = 33;
    var w = v;
    var h = v;
    var gap = 5;
    return (React.createElement(Box, { css: {
            columnCount: 2,
            columnGap: gap,
            width: '100%',
            animation: "".concat(scroll, " 3s ease-out infinite"),
        } },
        React.createElement(Box, { css: { width: w, flexDirection: 'column', '&>*': { marginBottom: gap } } },
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels })),
        React.createElement(Box, { css: { width: w, flexDirection: 'column', '&>*': { marginBottom: gap } } },
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels })),
        React.createElement(Box, { css: { width: w, flexDirection: 'column', '&>*': { marginBottom: gap } } },
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels })),
        React.createElement(Box, { css: { width: w, flexDirection: 'column', '&>*': { marginBottom: gap } } },
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels })),
        React.createElement(Box, { css: { width: w, flexDirection: 'column', '&>*': { marginBottom: gap } } },
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels })),
        React.createElement(Box, { css: { width: w, flexDirection: 'column', '&>*': { marginBottom: gap } } },
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels })),
        React.createElement(Box, { css: { width: w, flexDirection: 'column', '&>*': { marginBottom: gap } } },
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels })),
        React.createElement(Box, { css: { width: w, flexDirection: 'column', '&>*': { marginBottom: gap } } },
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: h, info: info, market: market, labels: labels }))));
};
var Vertical = function (_a) {
    var _b = _a.info, info = _b === void 0 ? false : _b, _c = _a.market, market = _c === void 0 ? false : _c, _d = _a.labels, labels = _d === void 0 ? false : _d;
    return (React.createElement(Box, { css: {
            columnCount: 5,
            columnGap: 10,
            width: '100%',
            animation: "".concat(scroll, " 3s ease-out infinite"),
        } },
        React.createElement(Box, { css: {
                flexDirection: 'column',
                width: '40%',
                margin: '0 auto',
                '&>*': { marginBottom: 15 },
            } },
            React.createElement(Thumb, { height: 100, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 70, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 100, info: info, market: market, labels: labels }),
            React.createElement(Thumb, { height: 70, info: info, market: market, labels: labels }))));
};

var getItem = function (prop) {
    return localStorage.getItem(prop);
};
var setItem = function (prop, value) {
    localStorage.setItem(prop, value);
    return getItem(prop);
};

var _a;
var Container = styled('div', (_a = {},
    _a["& ".concat(Select)] = {
        fontSize: 16,
        height: 32,
        '@bp1': {
            fontSize: 14,
        },
    },
    _a));

var SelectorThemeTypes;
(function (SelectorThemeTypes) {
    SelectorThemeTypes["system"] = "system";
    SelectorThemeTypes["dark"] = "dark";
    SelectorThemeTypes["light"] = "light";
})(SelectorThemeTypes || (SelectorThemeTypes = {}));
var SelectorTheme = function () {
    var _a = useState(SelectorThemeTypes.system), defaultValue = _a[0], setDefaultValue = _a[1];
    var changeTheme = function (theme) {
        setItem('--versum-theme', theme);
        setDefaultValue(theme);
        if (theme !== SelectorThemeTypes.system) {
            document.documentElement.dataset.theme = theme;
        }
        else {
            delete document.documentElement.dataset.theme;
        }
    };
    useEffect(function () {
        setDefaultValue(document.documentElement.dataset.theme || SelectorThemeTypes.system);
    }, []);
    var list = [
        { value: SelectorThemeTypes.system, label: 'System', icon: 'computer' },
        { value: SelectorThemeTypes.dark, label: 'Dark', icon: 'dark_mode' },
        { value: SelectorThemeTypes.light, label: 'Light', icon: 'light_mode' },
    ];
    return (React.createElement(Container, null,
        React.createElement(Selector, { value: defaultValue, list: list, onChange: changeTheme })));
};

var TextNumberPair = function (_a) {
    var _b = _a.address, address = _b === void 0 ? '' : _b, _c = _a.value, value = _c === void 0 ? 0 : _c, _d = _a.editable, editable = _d === void 0 ? true : _d, _e = _a.isPercentage, isPercentage = _e === void 0 ? false : _e, _f = _a.onChange, onChange = _f === void 0 ? function () { return null; } : _f;
    var wref = useRef(null);
    var vref = useRef(null);
    var handleChange = function () {
        var _a, _b;
        if (onChange) {
            if (wref.current && vref.current) {
                onChange((_a = wref.current) === null || _a === void 0 ? void 0 : _a.value, Number((_b = vref.current) === null || _b === void 0 ? void 0 : _b.value));
            }
        }
    };
    return (React.createElement(Box, { css: { flex: 1, display: 'flex', alignItems: 'center', columnGap: '$space$4' } },
        React.createElement(Input$1, { ref: wref, placeholder: "address", value: address, disabled: !editable, onChange: handleChange }),
        React.createElement(Input$1, __assign({ ref: vref, placeholder: "quantity", value: value, onChange: handleChange, type: "number", min: 0 }, (isPercentage && { max: 100 }), { small: true }))));
};

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger, AlertDialog, AudioPlayer, Box, BrowserFrame, Button$1 as Button, Checkbox, Code, Container$d as Container, Copy, DarkTheme, Dialog, Field, Grid, IconButton, Identicon, ImageEdit, Input$1 as Input, Label3D, LabelAUDIO, LabelGIF, LabelPDF, LabelVIDEO, LabelWEB, LightTheme, Logo, Masonry, Monogram, RadioGroup, Row, Search, Select as SelectStyles, Selector, SelectorTheme, SelectorThemeTypes, Slider, Status, Step, Table, Tag, Text, TextNumberPair, Textarea, Tezos, Thumb, Toggle, Tooltip, Vertical, css, getCssText, globalCss, keyframes, styled, theme };
